import { SphereTooltip } from "@components/common/sphere-tooltip";
import { SortDirection, SortItem, SortOption } from "@custom-types/sort-types";
import {
  blue,
  Button,
  IconButton,
  Menu,
  MenuSection,
  neutral,
  SingleSelectItem,
  SortDownIcon,
  SortUpIcon,
} from "@flat-ui";
import { alpha, Box, Divider } from "@mui/material";
import {
  DEFAULT_BOLD_TEXT_FONT_WEIGHT,
  DEFAULT_TEXT_FONT_SIZE,
} from "@styles/common-styles";
import { TableEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";

/** Information about a sort direction */
interface SortDirectionInfo {
  /** The label to display for the sort option */
  label: string;

  /** The icon to display for the sort option */
  icon: JSX.Element;

  /** The next sort direction when toggling */
  newSortDirection: SortDirection;
}

/** Information about each sort direction */
const SORT_DIRECTION_INFO: Record<SortDirection, SortDirectionInfo> = {
  asc: {
    label: "Ascending",
    icon: <SortUpIcon />,
    newSortDirection: "desc",
  },
  desc: {
    label: "Descending",
    icon: <SortDownIcon />,
    newSortDirection: "asc",
  },
};

interface Props<T extends string> {
  /** The available sort options */
  sortOptions: Record<T, SortOption<T>>;

  /** The current sort item */
  sortItem: SortItem<T>;

  /** Callback to update the sort item */
  setSortItem: (newSortItem: SortItem<T>) => void;

  /** The subject type for tracking purposes */
  subjectType: string;
}

/**
 * Component that allows users to select a sort field and toggle between ascending and descending directions.
 */
export function SortSelector<T extends string>({
  sortOptions,
  sortItem,
  setSortItem,
  subjectType,
}: Props<T>): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  /** Changes the sort direction and tracks the sort direction change event */
  function handleSortDirectionToggle(): void {
    const newSortDirection =
      SORT_DIRECTION_INFO[sortItem.sort].newSortDirection;

    // Track the sort order change event
    trackEvent({
      name: TableEvents.sortItems,
      props: {
        subject: subjectType,
        sortBy: sortItem.field,
        sortOrder: newSortDirection,
        viewMode: "cards",
      },
    });

    setSortItem({ field: sortItem.field, sort: newSortDirection });
  }

  /** Changes the sort field and tracks the sort field change event */
  function handleSortFieldChange(newField: T): void {
    // When changing the sort field, reset the sort direction to ascending
    const newSortDirection = "asc";

    trackEvent({
      name: TableEvents.sortItems,
      props: {
        subject: subjectType,
        sortBy: newField,
        sortOrder: newSortDirection,
        viewMode: "cards",
      },
    });

    setSortItem({ field: newField, sort: newSortDirection });

    // Close the menu after selecting an option
    setMenuAnchor(null);
  }

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: "4px",
        gap: "10px",
        overflow: "hidden",
        "& button": {
          height: "30px",
        },
        width: "fit-content",
        "&:hover": {
          // eslint-disable-next-line @typescript-eslint/no-magic-numbers
          backgroundColor: alpha(neutral[500], 0.15),
        },
      }}
    >
      <SphereTooltip title={SORT_DIRECTION_INFO[sortItem.sort].label}>
        <IconButton
          onClick={handleSortDirectionToggle}
          color={neutral[800]}
          disableRipple
          size="xs"
          sx={{
            minWidth: "auto",
            padding: "4px",
            "&:hover": {
              backgroundColor: "transparent",
              color: neutral[800],
            },
          }}
        >
          {SORT_DIRECTION_INFO[sortItem.sort].icon}
        </IconButton>
      </SphereTooltip>
      <Divider
        sx={{
          width: "1px",
          height: "18px",
          backgroundColor: neutral[300],
          alignSelf: "center",
        }}
      />
      <Button
        variant="ghost"
        data-testid="sort-label"
        size="m"
        onClick={(event) => setMenuAnchor(event.currentTarget)}
        sx={{
          color: menuAnchor !== null ? blue[500] : neutral[800],
          fontSize: DEFAULT_TEXT_FONT_SIZE,
          fontWeight: DEFAULT_BOLD_TEXT_FONT_WEIGHT,
          p: 0,
          flex: 1,
          justifyContent: "flex-start",
          "&:hover, &:focus": {
            backgroundColor: "transparent",
            color: blue[500],
          },
        }}
      >
        {sortOptions[sortItem.field].label}
      </Button>
      <Menu
        anchorEl={menuAnchor}
        open={menuAnchor !== null}
        onClose={() => setMenuAnchor(null)}
        minWidth="200px"
      >
        <MenuSection>SORT BY</MenuSection>
        {Object.values<SortOption<T>>(sortOptions).map((option) => (
          <SingleSelectItem
            key={option.field}
            label={option.label}
            value={option.field}
            selectedValue={sortItem.field}
            onClick={() => handleSortFieldChange(option.field)}
          />
        ))}
      </Menu>
    </Box>
  );
}
